import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { toast } from "sonner"

export interface FinancialExpense {
  id: string
  condominium_id: string
  description: string
  amount_cents: number
  currency: string
  category: string
  expense_date: string
  due_date?: string
  payment_date?: string
  supplier_id?: string
  payment_method?: string
  status: 'pending' | 'paid' | 'overdue' | 'canceled'
  invoice_number?: string
  invoice_url?: string
  receipt_url?: string
  notes?: string
  created_by?: string
  approved_by?: string
  approved_at?: string
  created_at: string
  updated_at: string
  supplier?: {
    name: string
    category: string
  }
}

export const useFinancialExpenses = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["financial-expenses", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return []

      const { data, error } = await supabase
        .from("financial_expenses")
        .select(`
          *,
          supplier:suppliers(name, category)
        `)
        .eq("condominium_id", profile.condominium_id)
        .order("expense_date", { ascending: false })

      if (error) throw error
      return data as FinancialExpense[]
    },
    enabled: !!profile?.condominium_id
  })
}

export const useCreateFinancialExpense = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (expenseData: Omit<FinancialExpense, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'condominium_id' | 'supplier'>) => {
      if (!profile?.id || !profile?.condominium_id) {
        throw new Error("Usuário não autenticado")
      }

      const { data, error } = await supabase
        .from("financial_expenses")
        .insert({
          ...expenseData,
          created_by: profile.id,
          condominium_id: profile.condominium_id
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-expenses"] })
      toast.success("Despesa registrada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao registrar despesa:", error)
      toast.error("Erro ao registrar despesa")
    }
  })
}

export const useUpdateFinancialExpense = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FinancialExpense> }) => {
      const { data, error } = await supabase
        .from("financial_expenses")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-expenses"] })
      toast.success("Despesa atualizada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar despesa:", error)
      toast.error("Erro ao atualizar despesa")
    }
  })
}

export const useDeleteFinancialExpense = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("financial_expenses")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-expenses"] })
      toast.success("Despesa removida com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao remover despesa:", error)
      toast.error("Erro ao remover despesa")
    }
  })
}

export const useExpenseStats = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["expense-stats", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return null

      const { data, error } = await supabase
        .from("financial_expenses")
        .select("status, category, amount_cents")
        .eq("condominium_id", profile.condominium_id)

      if (error) throw error

      const stats = data.reduce((acc, expense) => {
        acc.total_amount += expense.amount_cents
        
        if (expense.status === 'paid') {
          acc.paid_amount += expense.amount_cents
        } else if (expense.status === 'pending') {
          acc.pending_amount += expense.amount_cents
        } else if (expense.status === 'overdue') {
          acc.overdue_amount += expense.amount_cents
        }
        
        acc.by_category[expense.category] = (acc.by_category[expense.category] || 0) + expense.amount_cents
        
        return acc
      }, {
        total_amount: 0,
        paid_amount: 0,
        pending_amount: 0,
        overdue_amount: 0,
        by_category: {} as Record<string, number>
      })

      return stats
    },
    enabled: !!profile?.condominium_id
  })
}
